/**
 * A vertical exclusive choice with room for a clarifier under each option —
 * E4a's "Shunt outside the room / 1 per room" pair, M5's room location, B3's
 * building type.
 *
 * Options are `{ value, label, hint }`, which is `QuestionOption` from
 * src/core/types.ts minus its `formSets` tag. Callers pass
 * `visibleOptions(question, formSet)` (catalog.ts) — filtering by form set is
 * the catalog's job, never this component's.
 */

import type { ReactNode } from 'react';

export interface RadioOption {
  value: string;
  label: string;
  hint?: string;
}

export interface RadioListProps {
  options: readonly RadioOption[];
  value: string | undefined;
  onChange: (next: string) => void;
  /** Accessible name for the group; usually the question label. */
  ariaLabel: string;
  disabled?: boolean;
}

export function RadioList({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: RadioListProps): ReactNode {
  return (
    <div role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          className="radio"
          aria-checked={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          <span className="radio__mark" aria-hidden="true" />
          <span className="radio__text">
            {option.label}
            {/* The clarifier under an option tells the surveyor which answer to
             * pick — an instruction, not a caption (standard item 6). */}
            {option.hint ? <small data-legibility="helper">{option.hint}</small> : null}
          </span>
        </button>
      ))}
    </div>
  );
}
